import { type RefObject, useEffect, useRef } from 'react';

type UseProxyScrollArgs = {
  proxyRef: RefObject<HTMLDivElement | null>;
  headerTableRef: RefObject<HTMLTableElement | null>;
  bodyTableRef: RefObject<HTMLTableElement | null>;
  /** ピン留めセルのDOM検索スコープとなるコンテナRef */
  containerRef: RefObject<HTMLDivElement | null>;
};

type UseProxyScrollReturn = {
  scrollLeft: RefObject<number>;
};

export const useProxyScroll = ({
  proxyRef,
  headerTableRef,
  bodyTableRef,
  containerRef,
}: UseProxyScrollArgs): UseProxyScrollReturn => {
  const scrollLeftRef = useRef(0);
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    const proxy = proxyRef.current;
    if (!proxy) return;

    // willChange はブラウザへの最適化ヒント。初期化時に1回だけ設定
    if (headerTableRef.current) headerTableRef.current.style.willChange = 'transform';
    if (bodyTableRef.current) bodyTableRef.current.style.willChange = 'transform';

    const updateTransform = () => {
      const sl = proxy.scrollLeft;
      scrollLeftRef.current = sl;
      const value = `translate3d(${-sl}px, 0, 0)`;

      if (headerTableRef.current) {
        headerTableRef.current.style.transform = value;
      }
      if (bodyTableRef.current) {
        bodyTableRef.current.style.transform = value;
      }

      // ピン留めセルのtransform更新
      // ヘッダーテーブルとボディテーブルの直接のセルのみ対象（ネストテーブル内のセルは除外）
      const tables = [headerTableRef.current, bodyTableRef.current].filter(
        (t): t is HTMLTableElement => t !== null,
      );
      const containerWidth = containerRef.current?.clientWidth ?? 0;
      for (const table of tables) {
        const pinnedLeftCells = table.querySelectorAll<HTMLElement>(
          ':scope > thead > tr > [data-pinned-left], :scope > tbody > tr > [data-pinned-left]',
        );
        pinnedLeftCells.forEach((cell) => {
          const offset = Number(cell.getAttribute('data-pinned-left'));
          cell.style.transform = `translateX(${sl + offset}px)`;
        });
        const pinnedRightCells = table.querySelectorAll<HTMLElement>(
          ':scope > thead > tr > [data-pinned-right], :scope > tbody > tr > [data-pinned-right]',
        );
        pinnedRightCells.forEach((cell) => {
          const offset = Number(cell.getAttribute('data-pinned-right'));
          // 右ピン留め: コンテナの右端から offset 分内側にセルの右端を固定
          const cellLeft = cell.offsetLeft;
          const cellWidth = cell.offsetWidth;
          const targetLeft = containerWidth - cellWidth - offset;
          cell.style.transform = `translateX(${targetLeft - cellLeft + sl}px)`;
        });
      }

      rafIdRef.current = null;
    };

    const handleScroll = () => {
      if (rafIdRef.current !== null) return;
      rafIdRef.current = requestAnimationFrame(updateTransform);
    };

    proxy.addEventListener('scroll', handleScroll, { passive: true });

    // 初期レンダリング時にピン留めセルの位置を設定
    updateTransform();

    // テーブル行の変更（スケルトン→データ行など）を検知してピン留め位置を再計算
    const mutationRafIdRef = { current: null as number | null };
    const handleMutation = () => {
      if (mutationRafIdRef.current !== null) return;
      mutationRafIdRef.current = requestAnimationFrame(() => {
        updateTransform();
        mutationRafIdRef.current = null;
      });
    };
    const observer = new MutationObserver(handleMutation);
    // tbodyの直接の子要素（<tr>）の追加/削除のみ検知（subtree不要）
    const tbodies = [
      bodyTableRef.current?.querySelector(':scope > tbody'),
      headerTableRef.current?.querySelector(':scope > tbody'),
    ].filter((el): el is HTMLTableSectionElement => el !== null);
    for (const tbody of tbodies) {
      observer.observe(tbody, { childList: true });
    }

    // コンテナ幅の変更時に右ピン留めセルの位置を再計算（rAFで集約）
    const container = containerRef.current;
    const resizeObserver = new ResizeObserver(handleMutation);
    if (container) resizeObserver.observe(container);

    // コンテナ上のwheelイベントをプロキシに転送（トラックパッド2本指スワイプ対応）
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaX === 0) return;
      e.preventDefault();
      proxy.scrollLeft += e.deltaX;
    };
    container?.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      proxy.removeEventListener('scroll', handleScroll);
      container?.removeEventListener('wheel', handleWheel);
      observer.disconnect();
      resizeObserver.disconnect();
      if (headerTableRef.current) headerTableRef.current.style.willChange = '';
      if (bodyTableRef.current) bodyTableRef.current.style.willChange = '';
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      if (mutationRafIdRef.current !== null) {
        cancelAnimationFrame(mutationRafIdRef.current);
      }
    };
  }, [proxyRef, headerTableRef, bodyTableRef, containerRef]);

  return { scrollLeft: scrollLeftRef };
};
